import { useSelector } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { requireContractProductAtLeast } from '../../../components/Misc/RestrictedProduct'
import { parseDate, now } from '../../../config/dayjsConfig'
import { generateDataIndex } from '../../../components/Table/utils'
import { ContractProduct } from '../../../types/contract/Contract'
import { FiscalYear } from '../../../types/fiscalYear/FiscalYear'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { contextContractSelector } from '../contract/selectors'
import { filtersSelector } from '../filters/selectors'
import { periodGroupLabel, periodLabel } from '../fiscalYears/utils'
import { ReportDataType, ReportDataTypeCumulatives } from '../reports/types'
import { columnPeriodsSelector } from './selectors'
import { periodColumns } from './utils'
import { useCtxReportablePeriodGroups } from '../../../features/fiscalYear/hooks/useCtxReportablePeriodGroups'
import { useSelectedFiscalYears } from '../../../features/fiscalYear/hooks/useSelectedFiscalYears'
import { filterGroupsBySelectedFiscalYears, orderPeriodBlocks } from '../../../features/fiscalYear/utils'

export interface Column {
  fiscalYear?: FiscalYear
  year?: number
  month?: number
  label: string
  dataType?: ReportDataType | ReportDataTypeCumulatives
  periodGroup?: PeriodGroup | FiscalYear
  className?: string
  cumulative?: 'cumulative' | 'diff' | 'comparativeDiff'
  expandable: boolean
  dataIndex: string
  dataKey?: string | number // Key for accessing data from row[dataType].groups or row[dataType].elements
}

export const useTableColumns = (enableCumulatives?: boolean): Column[] => {
  const [tableColumns, setTableColumns] = useState<Column[]>([])
  const {
    dataTypes,
    periodGroup: periodGroupFilter,
    cumulativeFilter,
    budgetingScenario
  } = useSelector(filtersSelector)
  const contract = useSelector(contextContractSelector)
  const budgetCategoriesEnabled =
    contract?.product && requireContractProductAtLeast(contract.product, ContractProduct.BASIC)
  const periods = useSelector(columnPeriodsSelector)
  const groups = useCtxReportablePeriodGroups()
  const montlhyCategoriesEnabled = true
  const forecastCursor =
    (budgetingScenario?.forecastCursor && parseDate(budgetingScenario?.forecastCursor)) || undefined

  const filteredPeriodGroupDataTypes = (group: FiscalYear | PeriodGroup) => {
    let periodGroupDataTypes: ReportDataType[] = dataTypes.filter(d => d !== ReportDataType.comparativePeriod)

    if (!budgetCategoriesEnabled || ('budgetable' in group && !group?.budgetable)) {
      periodGroupDataTypes = periodGroupDataTypes.filter(
        d => d !== ReportDataType.budget && d !== ReportDataType.forecast
      )
    }

    const startDate = parseDate(group.startDate)
    if (startDate && startDate.isAfter(now())) {
      periodGroupDataTypes = periodGroupDataTypes.filter(d => d !== ReportDataType.actuals)
    }

    return periodGroupDataTypes
  }

  const filteredPeriodDataTypes = (group: FiscalYear | PeriodGroup) => {
    let periodGroupDataTypes: ReportDataType[] = dataTypes

    if (!budgetCategoriesEnabled || ('budgetable' in group && !group?.budgetable)) {
      periodGroupDataTypes = periodGroupDataTypes.filter(
        d => d !== ReportDataType.budget && d !== ReportDataType.forecast
      )
    }

    return periodGroupDataTypes
  }

  useEffect(() => {
    if (periodGroupFilter && periods) {
      const columns: Column[] = []
      // kk data ja columnit
      for (const period of periods) {
        for (const dataType of filteredPeriodDataTypes(periodGroupFilter)) {
          columns.push(periodColumns(period, dataType, forecastCursor))
        }

        if (enableCumulatives && cumulativeFilter?.month === period.month && cumulativeFilter?.year === period.year) {
          // kumulatiivit
          for (const dataType of filteredPeriodDataTypes(periodGroupFilter)) {
            columns.push({
              label: periodLabel(period, dataType, 'cumulative'),
              year: period.year,
              month: period.month,
              dataType,
              cumulative: 'cumulative',
              className: `${dataType}-cumulative-column`,
              expandable: true,
              dataIndex: generateDataIndex(dataType, undefined, period.year, period.month, 'cumulative'),
              dataKey: parseInt(`${period.year}${period.month.toString().padStart(2, '0')}00`, 10)
            })
          }

          if (
            filteredPeriodDataTypes(periodGroupFilter).includes(ReportDataType.actuals) &&
            filteredPeriodDataTypes(periodGroupFilter).includes(ReportDataType.budget)
          ) {
            columns.push({
              label: periodLabel(period, undefined, 'diff'),
              year: period.year,
              month: period.month,
              cumulative: 'diff',
              className: 'diff-column',
              expandable: true,
              dataIndex: generateDataIndex(undefined, undefined, period.year, period.month, 'diff'),
              dataKey: parseInt(`${period.year}${period.month.toString().padStart(2, '0')}00`, 10)
            })
          }

          if (
            filteredPeriodDataTypes(periodGroupFilter).includes(ReportDataType.actuals) &&
            filteredPeriodDataTypes(periodGroupFilter).includes(ReportDataType.comparativePeriod)
          ) {
            columns.push({
              label: periodLabel(period, ReportDataType.comparativePeriod, 'comparativeDiff'),
              year: period.year,
              month: period.month,
              cumulative: 'comparativeDiff',
              className: 'comparative-diff-column',
              expandable: true,
              dataIndex: generateDataIndex(undefined, undefined, period.year, period.month, 'comparativeDiff'),
              dataKey: parseInt(`${period.year}${period.month.toString().padStart(2, '0')}00`, 10)
            })
          }
        }
      }
      // console.count('PERIOD COLUMNS')
      setTableColumns(columns)
    } else {
      // group data ja columnit
      const columns: Column[] = []
      for (const group of groups) {
        if (group.id > 0 && !dataTypes.includes(ReportDataType.comparativePeriod)) {
          for (const dataType of filteredPeriodGroupDataTypes(group)) {
            columns.push({
              label: periodGroupLabel(group, dataType),
              dataType,
              className: `${dataType}-column`,
              periodGroup: group,
              expandable: montlhyCategoriesEnabled,
              dataIndex: generateDataIndex(dataType, group.id),
              dataKey: group.id
            })
          }
        } else if (dataTypes.includes(ReportDataType.comparativePeriod)) {
          // kvartaaleille group tason vertailukausi, normaalisti ei ole vertailusaraketta group tasolla
          // vertailukaudet tulevat negatiivisella id:llä
          let dataTypesToMap = filteredPeriodGroupDataTypes(group)
          if (
            dataTypes.includes(ReportDataType.comparativePeriod) &&
            !dataTypes.includes(ReportDataType.actuals) &&
            group.id < 0
          ) {
            // jos filttereissä VAIN comparative -> mäpätään actualit kerran, josta saadaan negatiivisen
            // id:n avulla oikeat comparativet sarakkeiksi
            dataTypesToMap = [ReportDataType.actuals]
          }
          for (const dataType of dataTypesToMap) {
            columns.push({
              label: periodGroupLabel(group, dataType),
              dataType: group.id < 0 ? ReportDataType.comparativePeriod : dataType,
              className: group.id < 0 ? `${ReportDataType.comparativePeriod}-column` : `${dataType}-column`,
              periodGroup: group,
              expandable: montlhyCategoriesEnabled,
              dataIndex: generateDataIndex(dataType, group.id),
              dataKey: group.id
            })
          }
        }
      }
      // console.log('GROUP COLUMNS', columns)
      setTableColumns(columns)
    }
  }, [periodGroupFilter, dataTypes, periods, groups])

  return tableColumns
}

/**
 * Applies the fiscal year selection and column sort orders to table columns.
 * Kept separate from useTableColumns so charts using it are not affected.
 */
export const useDisplayedTableColumns = (columns: Column[]): Column[] => {
  const { groupSortOrder = 'asc', monthSortOrder = 'asc' } = useSelector(filtersSelector)
  const { selected: selectedFiscalYears } = useSelectedFiscalYears()

  return useMemo(() => {
    // Month level (drilled into one period group): period columns incl. their cumulative columns
    if (columns.some(column => column.month !== undefined)) {
      return orderPeriodBlocks(columns, column => `${column.year}-${column.month}`, monthSortOrder)
    }

    const groups = columns.flatMap(column => (column.periodGroup ? [column.periodGroup] : []))
    const shownGroupIds = new Set(filterGroupsBySelectedFiscalYears(groups, selectedFiscalYears).map(g => g.id))
    const shownColumns = columns.filter(column => !column.periodGroup || shownGroupIds.has(column.periodGroup.id))
    // Comparative groups have negative ids -> keyed by abs id so they stay next to their pair
    return orderPeriodBlocks(shownColumns, column => Math.abs(column.periodGroup?.id ?? 0), groupSortOrder)
  }, [columns, selectedFiscalYears, groupSortOrder, monthSortOrder])
}
